<template>
  <div class="news-page">

    <!-- News Filter -->
    <section class="news-filter py-5">
      <div class="container">
        <div class="filter-wrapper reveal">
          <div class="search-box">
            <i class="fas fa-search"></i>
            <input
              type="text"
              class="form-control"
              :placeholder="$t('news.search')"
              v-model="searchKeyword"
              @input="handleSearch"
            >
          </div>
          <div class="filter-tabs">
            <button
              class="filter-tab"
              :class="{ active: selectedCategory === 'all' }"
              @click="selectCategory('all')"
            >
              <i class="fas fa-th-large mr-2"></i>{{ $t('news.categories.all') }}
            </button>
            <button
              class="filter-tab"
              :class="{ active: selectedCategory === 'safety' }"
              @click="selectCategory('safety')"
            >
              <i class="fas fa-shield-alt mr-2"></i>{{ $t('news.categories.safety') }}
            </button>
            <button
              class="filter-tab"
              :class="{ active: selectedCategory === 'product' }"
              @click="selectCategory('product')"
            >
              <i class="fas fa-box mr-2"></i>{{ $t('news.categories.product') }}
            </button>
            <button
              class="filter-tab"
              :class="{ active: selectedCategory === 'industry' }"
              @click="selectCategory('industry')"
            >
              <i class="fas fa-chart-line mr-2"></i>{{ $t('news.categories.industry') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- News List -->
    <section class="news-list py-8 bg-light">
      <div class="container">
        <div class="row">
          <!-- Main Content -->
          <div class="col-lg-8">
            <div class="news-grid" v-if="filteredNews.length > 0">
              <div
                class="news-card reveal"
                v-for="(news, index) in filteredNews"
                :key="news.id"
                :style="{ transitionDelay: ((index % 3) * 0.1) + 's' }"
              >
                <div class="news-image img-hover-zoom">
                  <img :src="news.image" :alt="news.title">
                  <div class="news-badge">
                    <span class="badge-category">{{ getCategoryName(news.category) }}</span>
                  </div>
                </div>
                <div class="news-content">
                  <h3 class="news-title">{{ $t(news.titleKey) }}</h3>
                  <p class="news-excerpt">{{ $t(news.excerptKey) }}</p>
                  <div class="news-meta">
                    <span class="meta-item">
                      <i class="fas fa-calendar-alt mr-2"></i>{{ news.date }}
                    </span>
                    <span class="meta-item">
                      <i class="fas fa-eye mr-2"></i>{{ news.views }} {{ $t('news.modal.views') }}
                    </span>
                  </div>
                  <a href="#" class="news-link" @click.prevent="viewNews(news)">
                    {{ $t('news.readMore') }} <i class="fas fa-arrow-right ml-2"></i>
                  </a>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="empty-state reveal">
              <i class="fas fa-newspaper fa-4x"></i>
              <h3>{{ $t('news.emptyTitle') }}</h3>
              <p>{{ $t('news.emptyDesc') }}</p>
            </div>

            <!-- Pagination -->
            <nav class="pagination-wrapper reveal" v-if="totalPages > 1">
              <div class="pagination-info">
                {{ $t('news.pagination.total') }} {{ totalCount }} {{ $t('news.pagination.articles') }}
              </div>
              <ul class="pagination">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="prevPage">
                    <i class="fas fa-chevron-left"></i>
                  </a>
                </li>
                <li
                  class="page-item"
                  v-for="page in visiblePages"
                  :key="page"
                  :class="{ active: currentPage === page, ellipsis: page === '...' }"
                >
                  <a class="page-link" href="#" @click.prevent="page !== '...' && goToPage(page)">
                    {{ page }}
                  </a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                  <a class="page-link" href="#" @click.prevent="nextPage">
                    <i class="fas fa-chevron-right"></i>
                  </a>
                </li>
              </ul>
            </nav>
          </div>

          <!-- Sidebar -->
          <div class="col-lg-4">
            <!-- Contact Card -->
            <div class="sidebar-card reveal" style="transition-delay: 0.1s;">
              <div class="sidebar-header">
                <i class="fas fa-headset mr-2"></i>
                <h4>{{ $t('news.sidebar.contact') }}</h4>
              </div>
              <div class="sidebar-body">
                <ul class="contact-list">
                  <li>
                    <i class="fas fa-phone text-primary"></i>
                    <div>
                      <span class="label">{{ $t('news.sidebar.phone') }}</span>
                      <a href="tel:+8615163903660">+86 15163903660</a>
                    </div>
                  </li>
                  <li>
                    <i class="fas fa-envelope text-primary"></i>
                    <div>
                      <span class="label">{{ $t('news.sidebar.email') }}</span>
                      <a href="mailto:postmaster5@lyyllb.com">postmaster5@lyyllb.com</a>
                    </div>
                  </li>
                  <li>
                    <i class="fas fa-map-marker-alt text-primary"></i>
                    <div>
                      <span class="label">{{ $t('news.sidebar.address') }}</span>
                      <span>{{ $t('news.sidebar.addressValue') }}</span>
                    </div>
                  </li>
                </ul>
                <a href="/contact" class="btn btn-primary btn-block mt-4">
                  <i class="fas fa-paper-plane mr-2"></i>{{ $t('news.sidebar.contactForm') }}
                </a>
              </div>
            </div>

            <!-- Popular Posts -->
            <div class="sidebar-card reveal" style="transition-delay: 0.2s;">
              <div class="sidebar-header">
                <i class="fas fa-fire mr-2"></i>
                <h4>{{ $t('news.sidebar.popular') }}</h4>
              </div>
              <div class="sidebar-body">
                <div class="popular-post" v-for="post in popularPosts" :key="post.id">
                  <div class="post-image">
                    <img :src="post.image" :alt="post.title">
                  </div>
                  <div class="post-content">
                    <span class="post-category">{{ getCategoryName(post.category) }}</span>
                    <h5 class="post-title">{{ $t(post.titleKey) }}</h5>
                    <span class="post-date">{{ post.date }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Categories -->
            <div class="sidebar-card reveal" style="transition-delay: 0.3s;">
              <div class="sidebar-header">
                <i class="fas fa-folder mr-2"></i>
                <h4>{{ $t('news.sidebar.categories') }}</h4>
              </div>
              <div class="sidebar-body">
                <ul class="category-list">
                  <li>
                    <a href="#" @click.prevent="selectCategory('all')">
                      <span>{{ $t('news.categories.all') }}</span>
                      <span class="count">{{ news.length }}</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" @click.prevent="selectCategory('safety')">
                      <span><i class="fas fa-shield-alt mr-2"></i>{{ $t('news.categories.safety') }}</span>
                      <span class="count">{{ getCategoryCount('safety') }}</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" @click.prevent="selectCategory('product')">
                      <span><i class="fas fa-box mr-2"></i>{{ $t('news.categories.product') }}</span>
                      <span class="count">{{ getCategoryCount('product') }}</span>
                    </a>
                  </li>
                  <li>
                    <a href="#" @click.prevent="selectCategory('industry')">
                      <span><i class="fas fa-chart-line mr-2"></i>{{ $t('news.categories.industry') }}</span>
                      <span class="count">{{ getCategoryCount('industry') }}</span>
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Tags -->
<!--            <div class="sidebar-card reveal" style="transition-delay: 0.4s;">-->
<!--              <div class="sidebar-header">-->
<!--                <i class="fas fa-tags mr-2"></i>-->
<!--                <h4>热门标签</h4>-->
<!--              </div>-->
<!--              <div class="sidebar-body">-->
<!--                <div class="tag-cloud">-->
<!--                  <a href="#" class="tag" v-for="tag in tags" :key="tag" @click.prevent="searchByTag(tag)">-->
<!--                    {{ tag }}-->
<!--                  </a>-->
<!--                </div>-->
<!--              </div>-->
<!--            </div>-->
          </div>
        </div>
      </div>
    </section>

    <!-- News Detail Modal -->
    <div class="news-modal" v-if="showModal" @click.self="closeModal">
      <div class="modal-container">
        <button class="modal-close" @click="closeModal">
          <i class="fas fa-times"></i>
        </button>
        <div class="modal-content" v-if="selectedNews">
          <div class="modal-header">
            <img :src="selectedNews.image" :alt="selectedNews.title">
          </div>
          <div class="modal-body">
            <span class="modal-category">{{ getCategoryName(selectedNews.category) }}</span>
            <h2 class="modal-title">{{ $t(selectedNews.titleKey) }}</h2>
            <div class="modal-meta">
              <span><i class="fas fa-calendar-alt mr-2"></i>{{ selectedNews.date }}</span>
              <span><i class="fas fa-eye mr-2"></i>{{ selectedNews.views }} {{ $t('news.modal.views') }}</span>
            </div>
            <div class="modal-text">
              <p>{{ $t(selectedNews.excerptKey) }}</p>
              <p>{{ $t(selectedNews.contentKey) }}</p>
            </div>
            <div class="modal-share">
              <span>{{ $t('news.modal.share') }}</span>
              <a href="#" class="share-btn"><i class="fab fa-weixin"></i></a>
              <a href="#" class="share-btn"><i class="fab fa-weibo"></i></a>
              <a href="#" class="share-btn"><i class="fab fa-qq"></i></a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'News',
  data() {
    return {
      searchKeyword: '',
      selectedCategory: 'all',
      currentPage: 1,
      pageSize: 6,
      parallaxOffset: 0,
      pageLoaded: false,
      showModal: false,
      selectedNews: null,
      news: [
        {
          id: 1,
          category: 'safety',
          titleKey: 'news.articles.1.title',
          excerptKey: 'news.articles.1.excerpt',
          contentKey: 'news.articles.1.content',
          date: '2022-09-19',
          views: 1256,
          image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/b3c686f15001834810603935301/guklx3lUbr0A.jpg'
        },
        {
          id: 2,
          category: 'safety',
          titleKey: 'news.articles.2.title',
          excerptKey: 'news.articles.2.excerpt',
          contentKey: 'news.articles.2.content',
          date: '2022-09-19',
          views: 982,
          image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/6ca2bc125001834810609030076/QDELp4fQ0DcA.jpg'
        },
        {
          id: 3,
          category: 'product',
          titleKey: 'news.articles.3.title',
          excerptKey: 'news.articles.3.excerpt',
          contentKey: 'news.articles.3.content',
          date: '2022-09-19',
          views: 1567,
          image: 'https://1500062809.vod-qcloud.com/6d053ea1vodcq1500062809/f824da105001834810611341427/cHROcQncNH8A.jpg'
        },
      ]
    }
  },
  computed: {
    filteredNews() {
      let result = this.news

      if (this.selectedCategory !== 'all') {
        result = result.filter(n => n.category === this.selectedCategory)
      }

      if (this.searchKeyword) {
        result = result.filter(n =>
          n.title.includes(this.searchKeyword) ||
          n.excerpt.includes(this.searchKeyword)
        )
      }

      return result.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize)
    },
    totalCount() {
      let result = this.news
      if (this.selectedCategory !== 'all') {
        result = result.filter(n => n.category === this.selectedCategory)
      }
      if (this.searchKeyword) {
        result = result.filter(n =>
          n.title.includes(this.searchKeyword) ||
          n.excerpt.includes(this.searchKeyword)
        )
      }
      return result.length
    },
    totalPages() {
      return Math.ceil(this.totalCount / this.pageSize)
    },
    visiblePages() {
      const pages = []
      const total = this.totalPages
      const current = this.currentPage

      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i)
        }
      } else {
        if (current <= 4) {
          for (let i = 1; i <= 5; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        } else if (current >= total - 3) {
          pages.push(1)
          pages.push('...')
          for (let i = total - 4; i <= total; i++) {
            pages.push(i)
          }
        } else {
          pages.push(1)
          pages.push('...')
          for (let i = current - 1; i <= current + 1; i++) {
            pages.push(i)
          }
          pages.push('...')
          pages.push(total)
        }
      }
      return pages
    },
    popularPosts() {
      return [...this.news]
        .sort((a, b) => b.views - a.views)
        .slice(0, 3)
    }
  },
  mounted() {
    setTimeout(() => {
      this.pageLoaded = true
    }, 200)

    window.addEventListener('scroll', this.handleScroll)
    this.initRevealAnimations()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll)
  },
  methods: {
    handleScroll() {
      this.parallaxOffset = window.scrollY * 0.3
    },
    getCategoryName(category) {
      const map = {
        safety: 'news.categories.safety',
        product: 'news.categories.product',
        industry: 'news.categories.industry'
      }
      return this.$t(map[category]) || this.$t('news.categories.other')
    },
    getCategoryCount(category) {
      return this.news.filter(n => n.category === category).length
    },
    selectCategory(category) {
      this.selectedCategory = category
      this.currentPage = 1
    },
    handleSearch() {
      this.currentPage = 1
    },
    searchByTag(tag) {
      this.searchKeyword = tag
      this.selectedCategory = 'all'
      this.currentPage = 1
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    goToPage(page) {
      this.currentPage = page
      window.scrollTo({ top: 400, behavior: 'smooth' })
    },
    viewNews(news) {
      this.selectedNews = news
      this.showModal = true
      document.body.style.overflow = 'hidden'
    },
    closeModal() {
      this.showModal = false
      this.selectedNews = null
      document.body.style.overflow = ''
    },
    initRevealAnimations() {
      const reveals = document.querySelectorAll('.reveal')

      const revealOnScroll = () => {
        reveals.forEach(element => {
          const windowHeight = window.innerHeight
          const elementTop = element.getBoundingClientRect().top
          const revealPoint = 100

          if (elementTop < windowHeight - revealPoint) {
            element.classList.add('active')
          }
        })
      }

      window.addEventListener('scroll', revealOnScroll)
      window.addEventListener('load', revealOnScroll)

      revealOnScroll()
    }
  }
}
</script>

<style scoped>
.news-page {
  padding-top: 0;
}

/* Page Header */
.page-header {
  position: relative;
  height: 400px;
  overflow: hidden;
  display: flex;
  align-items: center;
}

.page-header-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 120%;
  background: linear-gradient(135deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.5) 100%),
              url('https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=News%20media%20business%20information%20background&image_size=landscape_16_9');
  background-size: cover;
  background-position: center;
  will-change: transform;
}

.page-header-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.4) 100%);
}

.page-header-content {
  position: relative;
  z-index: 2;
  width: 100%;
}

.page-header-text {
  text-align: center;
  color: white;
}

.page-title {
  font-size: 3.5rem;
  font-weight: 800;
  margin-bottom: 15px;
}

.page-title span {
  display: inline-block;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.page-title.animate-in span {
  opacity: 1;
  transform: translateY(0);
}

.page-subtitle {
  font-size: 1.2rem;
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease;
  transition-delay: 0.3s;
}

.page-subtitle.animate-in {
  opacity: 0.9;
  transform: translateY(0);
}

/* Filter Section */
.filter-wrapper {
  background: white;
  padding: 25px 30px;
  border-radius: 16px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  display: flex;
  align-items: center;
  gap: 30px;
}

.search-box {
  position: relative;
  flex: 1;
  max-width: 350px;
}

.search-box i {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
}

.search-box input {
  padding-left: 45px;
  border-radius: 10px;
  border: 2px solid #f0f0f0;
  transition: all 0.3s;
}

.search-box input:focus {
  border-color: #e53935;
  box-shadow: 0 0 0 3px rgba(229, 57, 53, 0.1);
}

.filter-tabs {
  display: flex;
  gap: 10px;
}

.filter-tab {
  padding: 10px 20px;
  background: #f8f9fa;
  border: none;
  border-radius: 10px;
  color: #666;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s;
}

.filter-tab:hover {
  background: #fff5f5;
  color: #e53935;
}

.filter-tab.active {
  background: linear-gradient(135deg, #e53935, #c62828);
  color: white;
}

/* News Grid */
.news-grid {
  display: flex;
  flex-direction: column;
  gap: 30px;
}

.news-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  display: flex;
  transition: all 0.4s ease;
}

.news-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 50px rgba(0,0,0,0.1);
}

.news-image {
  width: 350px;
  min-height: 250px;
  position: relative;
  overflow: hidden;
}

.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.news-card:hover .news-image img {
  transform: scale(1.1);
}

.news-badge {
  position: absolute;
  top: 15px;
  left: 15px;
}

.badge-category {
  display: inline-block;
  padding: 6px 15px;
  background: #e53935;
  color: white;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 500;
}

.news-content {
  padding: 30px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.news-title {
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.4;
}

.news-excerpt {
  font-size: 0.95rem;
  color: #666;
  line-height: 1.7;
  margin-bottom: 20px;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-meta {
  display: flex;
  gap: 25px;
  margin-bottom: 15px;
}

.meta-item {
  font-size: 0.9rem;
  color: #999;
  display: flex;
  align-items: center;
}

.news-link {
  color: #e53935;
  font-weight: 500;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  transition: all 0.3s;
}

.news-link:hover {
  color: #c62828;
}

.news-link i {
  transition: transform 0.3s;
}

.news-link:hover i {
  transform: translateX(5px);
}

/* Sidebar */
.sidebar-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0,0,0,0.05);
  margin-bottom: 30px;
}

.sidebar-header {
  background: linear-gradient(135deg, #1a1a2e, #16213e);
  padding: 18px 20px;
  color: white;
  display: flex;
  align-items: center;
}

.sidebar-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
}

.sidebar-body {
  padding: 20px;
}

/* Contact List */
.contact-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.contact-list li {
  display: flex;
  align-items: flex-start;
  gap: 15px;
  padding: 12px 0;
  border-bottom: 1px solid #f0f0f0;
}

.contact-list li:last-child {
  border-bottom: none;
}

.contact-list li i {
  font-size: 1.1rem;
  margin-top: 3px;
}

.contact-list li div {
  display: flex;
  flex-direction: column;
}

.contact-list li .label {
  font-size: 0.8rem;
  color: #999;
}

.contact-list li a, .contact-list li span:not(.label) {
  font-size: 0.95rem;
  color: #333;
  text-decoration: none;
}

.contact-list li a:hover {
  color: #e53935;
}

/* Popular Posts */
.popular-post {
  display: flex;
  gap: 15px;
  padding: 15px 0;
  border-bottom: 1px solid #f0f0f0;
}

.popular-post:last-child {
  border-bottom: none;
}

.post-image {
  width: 80px;
  height: 80px;
  border-radius: 10px;
  overflow: hidden;
  flex-shrink: 0;
}

.post-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.popular-post:hover .post-image img {
  transform: scale(1.1);
}

.post-content {
  flex: 1;
}

.post-category {
  font-size: 0.75rem;
  color: #e53935;
}

.post-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: #333;
  margin: 5px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-date {
  font-size: 0.8rem;
  color: #999;
}

/* Category List */
.category-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.category-list li {
  border-bottom: 1px solid #f0f0f0;
}

.category-list li:last-child {
  border-bottom: none;
}

.category-list a {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  color: #666;
  text-decoration: none;
  transition: all 0.3s;
}

.category-list a:hover {
  color: #e53935;
  padding-left: 5px;
}

.category-list .count {
  background: #f0f0f0;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
}

/* Tag Cloud */
.tag-cloud {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag {
  padding: 6px 15px;
  background: #f8f9fa;
  color: #666;
  border-radius: 20px;
  font-size: 0.85rem;
  text-decoration: none;
  transition: all 0.3s;
}

.tag:hover {
  background: #e53935;
  color: white;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 50px;
  padding-top: 30px;
  border-top: 1px solid #f0f0f0;
}

.pagination-info {
  color: #999;
  font-size: 0.9rem;
}

.pagination {
  display: flex;
  gap: 8px;
}

.page-item {
  list-style: none;
}

.page-link {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
  border: none;
  border-radius: 10px;
  color: #666;
  text-decoration: none;
  transition: all 0.3s;
}

.page-link:hover {
  background: #fff5f5;
  color: #e53935;
}

.page-item.active .page-link {
  background: linear-gradient(135deg, #e53935, #c62828);
  color: white;
}

.page-item.disabled .page-link {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-item.ellipsis .page-link {
  background: transparent;
  color: #999;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 20px;
}

.empty-state i {
  color: #ddd;
  margin-bottom: 20px;
}

.empty-state h3 {
  font-size: 1.5rem;
  color: #666;
  margin-bottom: 10px;
}

.empty-state p {
  color: #999;
}

/* Modal */
.news-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-container {
  background: white;
  border-radius: 24px;
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.modal-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 45px;
  height: 45px;
  background: white;
  border: none;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s;
  z-index: 10;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
}

.modal-close:hover {
  background: #e53935;
  color: white;
}

.modal-header {
  height: 300px;
  overflow: hidden;
}

.modal-header img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.modal-body {
  padding: 40px;
}

.modal-category {
  display: inline-block;
  padding: 6px 15px;
  background: #fff5f5;
  color: #e53935;
  border-radius: 20px;
  font-size: 0.85rem;
  margin-bottom: 15px;
}

.modal-title {
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 20px;
}

.modal-meta {
  display: flex;
  gap: 25px;
  color: #999;
  font-size: 0.9rem;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-text {
  font-size: 1rem;
  color: #666;
  line-height: 1.8;
}

.modal-text p {
  margin-bottom: 20px;
}

.modal-share {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  gap: 15px;
}

.share-btn {
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: all 0.3s;
}

.share-btn:hover {
  background: #e53935;
  color: white;
}

/* Utilities */
.py-5 {
  padding-top: 30px;
  padding-bottom: 30px;
}

.py-8 {
  padding-top: 80px;
  padding-bottom: 80px;
}

/* Reveal Animations */
.reveal {
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s cubic-bezier(0.4, 0, 0.2, 1);
}

.reveal.active {
  opacity: 1;
  transform: translateY(0);
}

/* Responsive */
@media (max-width: 991px) {
  .filter-wrapper {
    flex-direction: column;
    align-items: stretch;
  }

  .search-box {
    max-width: 100%;
  }

  .filter-tabs {
    flex-wrap: wrap;
  }

  .news-card {
    flex-direction: column;
  }

  .news-image {
    width: 100%;
    height: 200px;
  }
}

@media (max-width: 767px) {
  .page-title {
    font-size: 2.5rem;
  }

  .pagination-wrapper {
    flex-direction: column;
    gap: 20px;
  }

  .modal-body {
    padding: 25px;
  }

  .modal-title {
    font-size: 1.5rem;
  }
}
</style>
